import { ElementRef, Renderer2 } from '@angular/core';
import {
  BackgroundImageSize,
  BackgroundProperties,
  ComponentAlignment,
  ComponentBorder,
  ComponentRadius,
  ComponentShadow,
  DataRepeaterComponent,
  Direction,
  FlipAlignment,
  FourSideStylingEntity,
  IFrameOverflow,
  PositionCorner,
  ScratchProperties,
  TextAlignment,
  TextProperties,
  WidgetComponent,
  WidgetComponentSubType,
  WidgetDeviceView,
  WidgetPosition,
  WidgetState,
} from '../../../widgets.model';
import { AccountVariable, AccountVariableType } from '../../../../account-settings/account-settings-models';
import ColorHelpers from '../../../../shared/helpers/color-helpers';
import WidgetComponentHelpers from 'app/campaign/widget-component-helpers';

export default class Renderer2Helpers {
  static FlagsBugfixValue = 3;
  private static gradientTextShadowElements = new WeakSet<Element>();

  static updateStyle(elementRef: ElementRef, renderer: Renderer2) {
    renderer.setStyle(elementRef.nativeElement, 'display', 'block', Renderer2Helpers.FlagsBugfixValue);
  }

  static showElement(elementRef: ElementRef, renderer: Renderer2) {
    renderer.setStyle(elementRef.nativeElement, 'display', 'block', Renderer2Helpers.FlagsBugfixValue);
  }

  static hideElement(elementRef: ElementRef, renderer: Renderer2) {
    renderer.setStyle(elementRef.nativeElement, 'display', 'none', Renderer2Helpers.FlagsBugfixValue);
  }

  static updateColor(color: string, componentRef: any, renderer: Renderer2, accountVariables: Array<AccountVariable>) {
    const colorValue = Renderer2Helpers.extractColorFromProperty(color, accountVariables);
    renderer.setStyle(componentRef, 'color', colorValue, Renderer2Helpers.FlagsBugfixValue);
  }

  static updateOpacity(opacity: number, componentRef: any, renderer: Renderer2) {
    renderer.setStyle(componentRef, 'opacity', opacity, Renderer2Helpers.FlagsBugfixValue);
  }

  static updateComponentsText(
    component: WidgetComponent,
    componentRef: ElementRef,
    renderer: Renderer2,
    accountVariables: Array<AccountVariable>,
    deviceView: WidgetDeviceView,
    textOpacity?: number,
  ) {
    if (!componentRef) return;
    const fontSize =
      deviceView === WidgetDeviceView.Mobile && component.mobile && component.mobile?.fontSize
        ? component.mobile?.fontSize
        : component.textProperties?.fontSize;
    const lineHeight =
      deviceView === WidgetDeviceView.Mobile && component.mobile && component.mobile?.lineHeight
        ? component.mobile?.lineHeight
        : component.textProperties?.lineHeight;
    const letterSpacing =
      deviceView === WidgetDeviceView.Mobile && component.mobile && component.mobile?.letterSpacing
        ? component.mobile?.letterSpacing
        : component.textProperties?.letterSpacing;
    if (component.textProperties) {
      let textColor = Renderer2Helpers.extractColorFromProperty(component.textProperties.color, accountVariables);
      if (textOpacity && textOpacity < 1) {
        textColor = this.addAlphaToHexColor(textColor, textOpacity);
      }
      Renderer2Helpers.updateFontSize(fontSize, componentRef?.nativeElement || componentRef, renderer);
      Renderer2Helpers.updateLineHeight(lineHeight, componentRef, renderer);
      Renderer2Helpers.updateLetterSpacing(letterSpacing, componentRef, renderer);
      Renderer2Helpers.updateFontColor(textColor, componentRef?.nativeElement || componentRef, renderer);
      Renderer2Helpers.updateFontWeight(component.textProperties.fontWeight, componentRef, renderer);
      Renderer2Helpers.updateFontFamily(component.textProperties.font, componentRef, renderer);
      Renderer2Helpers.updateTextAlignment(component.textProperties.alignment, componentRef, renderer);
      Renderer2Helpers.updateTextShadow(
        component.textProperties.textShadow,
        componentRef?.nativeElement || componentRef,
        renderer,
        accountVariables,
        textColor,
      );
    }
  }

  static addAlphaToHexColor(color: string, opacity: number): string {
    if (!color) {
      return color;
    }

    // coerce values so it is between 0 and 1.
    if (color.indexOf('#') !== 0 || !opacity || opacity >= 1) {
      return color;
    }
    let colorCode = color.substring(1);
    if (colorCode.length === 8) {
      colorCode = colorCode.substring(0, 6);
    }
    const _opacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
    return '#' + colorCode + _opacity.toString(16).toUpperCase();
  }

  static updateComponentsLabel(
    component: WidgetComponent,
    componentRef: ElementRef,
    renderer: Renderer2,
    accountVariables: Array<AccountVariable>,
  ) {
    if (component.label) {
      const fontSize = component.label?.fontSize;
      const lineHeight = component.label?.lineHeight;
      const textColor = Renderer2Helpers.extractColorFromProperty(component.label.color, accountVariables);
      Renderer2Helpers.updateFontColor(textColor, componentRef.nativeElement, renderer);
      Renderer2Helpers.updateFontSize(fontSize, componentRef.nativeElement, renderer);
      Renderer2Helpers.updateLineHeight(lineHeight, componentRef, renderer);
      Renderer2Helpers.updateLetterSpacing(component.label.letterSpacing, componentRef, renderer);
      Renderer2Helpers.updateFontWeight(component.label.fontWeight, componentRef, renderer);
      Renderer2Helpers.updateFontFamily(component.label.font, componentRef, renderer);
      Renderer2Helpers.updateTextAlignment(component.label.alignment, componentRef, renderer);
      Renderer2Helpers.updateOrRemoveMargin(component.label?.margin, componentRef.nativeElement, renderer);
    }
  }

  static updateComponentsTextProperties(
    textProperties: TextProperties,
    elementRef: any,
    renderer: Renderer2,
    accountVariables: Array<AccountVariable>,
  ) {
    if (textProperties) {
      const textColor = Renderer2Helpers.extractColorFromProperty(textProperties.color, accountVariables);
      this.updateFontColor(textColor, elementRef, renderer);
      renderer.setStyle(elementRef, 'font-size', textProperties.fontSize, Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(elementRef, 'line-height', textProperties.lineHeight, Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(elementRef, 'font-family', textProperties.font, Renderer2Helpers.FlagsBugfixValue);
      const textAlign = textProperties.alignment ? textProperties.alignment : 'unset';
      renderer.setStyle(elementRef, 'text-align', textAlign, Renderer2Helpers.FlagsBugfixValue);
    }
  }

  static isColorAccountVariable(colorString: string): boolean {
    if (!colorString) {
      return false;
    }
    return /{[A-Za-z][0-9A-Za-z_ ]+}/.test(colorString);
  }

  static extractColorFromProperty(colorProperty: string, accountVariables: Array<AccountVariable>): string {
    let colorValue = undefined;
    if (colorProperty) {
      if (Renderer2Helpers.isColorAccountVariable(colorProperty)) {
        if (accountVariables) {
          const colorKey = colorProperty.replace('{', '').replace('}', '');
          const foundColorFromVariable = accountVariables
            .filter((v) => v.type === AccountVariableType.Color)
            .find((v) => v.key.toLowerCase() === colorKey.toLowerCase());
          if (foundColorFromVariable) {
            colorValue = foundColorFromVariable.value;
          }
        }
      } else {
        colorValue = colorProperty;
      }
    }
    return colorValue;
  }

  static updateComponentsAppearance(
    component: WidgetComponent,
    componentRef: ElementRef,
    renderer: Renderer2,
    accountVariables: Array<AccountVariable>,
    deviceView: WidgetDeviceView,
    inputContainerRef?: ElementRef,
  ) {
    Renderer2Helpers.updateOrRemoveBackground(
      component.background,
      componentRef,
      renderer,
      accountVariables,
      component.subType,
    );
    Renderer2Helpers.updateOrRemoveBorder(component.border, componentRef, renderer, accountVariables);
    Renderer2Helpers.updateOrRemoveRadius(component.radius, componentRef, renderer);
    const margin =
      deviceView === WidgetDeviceView.Mobile && component.mobile && component.mobile.margin
        ? component.mobile.margin
        : component.margin;
    if (inputContainerRef) {
      Renderer2Helpers.updateOrRemoveMargin(margin, inputContainerRef, renderer);
    } else {
      Renderer2Helpers.updateOrRemoveMargin(margin, componentRef, renderer);
    }
    const padding =
      deviceView === WidgetDeviceView.Mobile && component.mobile && component.mobile.padding
        ? component.mobile.padding
        : component.padding;
    Renderer2Helpers.updateOrRemovePadding(padding, componentRef, renderer);
    if (component.subType === WidgetComponentSubType.TextFloating) {
      Renderer2Helpers.updateOrRemoveDropShadow(component.shadow, componentRef, renderer, accountVariables);
      this.removeShadow(componentRef, renderer);
    } else {
      Renderer2Helpers.updateOrRemoveShadow(component.shadow, componentRef, renderer, accountVariables);
    }
  }

  static updateDataRepeaterItemAppearance(
    component: DataRepeaterComponent,
    componentRef: ElementRef,
    renderer: Renderer2,
    accountVariables: Array<AccountVariable>,
    deviceView: WidgetDeviceView,
  ) {
    Renderer2Helpers.updateOrRemoveBackground(component.itemBackground, componentRef, renderer, accountVariables);
    Renderer2Helpers.updateOrRemoveBorder(component.itemBorder, componentRef, renderer, accountVariables);
    Renderer2Helpers.updateOrRemoveRadius(component.itemRadius, componentRef, renderer);
    const margin =
      deviceView === WidgetDeviceView.Mobile && component.itemMobile && component.itemMobile.margin
        ? component.itemMobile.margin
        : component.itemMargin;
    Renderer2Helpers.updateOrRemoveMargin(margin, componentRef, renderer);
    const padding =
      deviceView === WidgetDeviceView.Mobile && component.itemMobile && component.itemMobile.padding
        ? component.itemMobile.padding
        : component.itemPadding;
    Renderer2Helpers.updateOrRemovePadding(padding, componentRef, renderer);
    Renderer2Helpers.updateOrRemoveShadow(component.itemShadow, componentRef, renderer, accountVariables);
  }

  private static removeCopies(componentRef: ElementRef) {
    if (componentRef.nativeElement.children.length > 1) {
      while (componentRef.nativeElement.children.length > 1) {
        componentRef.nativeElement.removeChild(componentRef.nativeElement.lastChild);
      }
    }
  }

  static toggleShowCopies(componentRef: ElementRef, itemRef: ElementRef, amountOfCopies: number, show: boolean) {
    this.removeCopies(componentRef);
    if (show) {
      for (let i = 1; i < amountOfCopies; i++) {
        const copy = itemRef.nativeElement.cloneNode(true) as HTMLElement;
        const handlerTools = Array.from(copy.querySelectorAll('app-handler-tool'));
        for (const tool of handlerTools) {
          tool.remove();
        }
        const moveIcons = Array.from(copy.querySelectorAll('app-widget-component-move-icons'));
        for (const moveIcon of moveIcons) {
          moveIcon.remove();
        }
        const components = Array.from(copy.querySelectorAll('.widget-component'));
        for (const component of components) {
          component.classList.remove('edit-mode');
        }
        copy.classList.add('component-data-repeater-item-disabled');
        componentRef.nativeElement.appendChild(copy);
      }
    }
  }

  static removeAllComponentAppearance(componentRef: any, renderer: Renderer2) {
    Renderer2Helpers.removeBackground(componentRef, renderer);
    Renderer2Helpers.removeBorder(componentRef, renderer);
    Renderer2Helpers.removeRadius(componentRef, renderer);
    Renderer2Helpers.removeMargin(componentRef, renderer);
    Renderer2Helpers.removePadding(componentRef, renderer);
    Renderer2Helpers.removeShadow(componentRef, renderer);
  }

  static updateFontColor(fontColor: string, componentRef: ElementRef, renderer: Renderer2) {
    if (!componentRef) return;
    if (fontColor?.includes('gradient')) {
      renderer.removeStyle(componentRef, 'color', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(componentRef, 'background-image', fontColor, Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(componentRef, '-webkit-background-clip', 'text', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(componentRef, '-webkit-text-fill-color', 'transparent', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(componentRef, 'background-clip', 'text', Renderer2Helpers.FlagsBugfixValue);
    } else {
      const el: HTMLElement = (componentRef as any)?.nativeElement ?? componentRef;
      if (
        el?.style?.backgroundImage?.includes('gradient') &&
        el?.style?.getPropertyValue('-webkit-background-clip') === 'text'
      ) {
        renderer.removeStyle(componentRef, 'background-image', Renderer2Helpers.FlagsBugfixValue);
      }
      renderer.removeStyle(componentRef, '-webkit-background-clip', Renderer2Helpers.FlagsBugfixValue);
      renderer.removeStyle(componentRef, '-webkit-text-fill-color', Renderer2Helpers.FlagsBugfixValue);
      renderer.removeStyle(componentRef, 'background-clip', Renderer2Helpers.FlagsBugfixValue);
      if (fontColor) {
        renderer.setStyle(componentRef, 'color', fontColor, Renderer2Helpers.FlagsBugfixValue);
      } else {
        renderer.removeStyle(componentRef, 'color', Renderer2Helpers.FlagsBugfixValue);
      }
    }
  }

  static updateSvgColor(fontColor: string, componentRef: ElementRef, renderer: Renderer2) {
    if (fontColor) {
      renderer.setStyle(componentRef, 'fill', fontColor, Renderer2Helpers.FlagsBugfixValue);
    }
  }

  static updateFilterColor(fontColor: string, componentRef: ElementRef, renderer: Renderer2) {
    renderer.setStyle(componentRef, 'filter', fontColor);
  }

  static updateFontSize(fontSize: string, componentRef: ElementRef, renderer: Renderer2) {
    if (fontSize) {
      renderer.setStyle(componentRef, 'font-size', fontSize, Renderer2Helpers.FlagsBugfixValue);
    } else {
      renderer.removeStyle(componentRef, 'font-size', Renderer2Helpers.FlagsBugfixValue);
    }
  }

  static updateLineHeight(lineHeight: string, componentRef: ElementRef, renderer: Renderer2) {
    const target = componentRef.nativeElement || componentRef;
    if (lineHeight) {
      renderer.setStyle(target, 'line-height', lineHeight, Renderer2Helpers.FlagsBugfixValue);
    } else {
      renderer.removeStyle(target, 'line-height', Renderer2Helpers.FlagsBugfixValue);
    }
  }

  static updateLetterSpacing(letterSpacing: string, componentRef: ElementRef, renderer: Renderer2) {
    const target = componentRef.nativeElement || componentRef;
    if (letterSpacing) {
      renderer.setStyle(target, 'letter-spacing', letterSpacing, Renderer2Helpers.FlagsBugfixValue);
    } else {
      renderer.removeStyle(target, 'letter-spacing', Renderer2Helpers.FlagsBugfixValue);
    }
  }

  static updateFontFamily(fontFamily: string, componentRef: ElementRef, renderer: Renderer2) {
    const target = componentRef.nativeElement || componentRef;
    if (fontFamily) {
      renderer.setStyle(target, 'font-family', fontFamily, Renderer2Helpers.FlagsBugfixValue);
    } else {
      renderer.removeStyle(target, 'font-family', Renderer2Helpers.FlagsBugfixValue);
    }
  }

  static updateFontWeight(fontWeight: number, componentRef: ElementRef, renderer: Renderer2) {
    const elem = componentRef?.nativeElement || componentRef;
    if (fontWeight) {
      renderer.setStyle(elem, 'font-weight', fontWeight, Renderer2Helpers.FlagsBugfixValue);
    } else if (elem) {
      renderer.removeStyle(elem, 'font-weight', Renderer2Helpers.FlagsBugfixValue);
    }
  }

  static updateTextShadow(
    textShadow: ComponentShadow | undefined,
    componentRef: ElementRef,
    renderer: Renderer2,
    accountVariables: Array<AccountVariable>,
    fontColor?: string,
  ) {
    const target = componentRef.nativeElement || componentRef;
    const isGradient = ColorHelpers.isColorGradient(fontColor);

    if (textShadow) {
      const color = Renderer2Helpers.extractColorFromProperty(textShadow.color || '', accountVariables);
      const h = textShadow.horizontalLength || '0';
      const v = textShadow.verticalLength || '0';
      const blur = textShadow.blur || '0';
      const shadowString = `${h} ${v} ${blur} ${color || '#000000'}`;

      if (isGradient) {
        renderer.removeStyle(target, 'text-shadow', Renderer2Helpers.FlagsBugfixValue);
        renderer.setStyle(target, 'filter', `drop-shadow(${shadowString})`, Renderer2Helpers.FlagsBugfixValue);
        Renderer2Helpers.gradientTextShadowElements.add(target);
      } else {
        if (Renderer2Helpers.gradientTextShadowElements.has(target)) {
          renderer.removeStyle(target, 'filter', Renderer2Helpers.FlagsBugfixValue);
          Renderer2Helpers.gradientTextShadowElements.delete(target);
        }
        renderer.setStyle(target, 'text-shadow', shadowString, Renderer2Helpers.FlagsBugfixValue);
      }
    } else {
      renderer.removeStyle(target, 'text-shadow', Renderer2Helpers.FlagsBugfixValue);
      if (Renderer2Helpers.gradientTextShadowElements.has(target)) {
        renderer.removeStyle(target, 'filter', Renderer2Helpers.FlagsBugfixValue);
        Renderer2Helpers.gradientTextShadowElements.delete(target);
      }
    }
  }

  static updateTextAlignment(alignment: TextAlignment, componentRef: ElementRef, renderer: Renderer2) {
    if (alignment && componentRef.nativeElement) {
      const textAlign = alignment ? alignment : 'unset';
      renderer.setStyle(componentRef.nativeElement, 'text-align', textAlign, Renderer2Helpers.FlagsBugfixValue);
    }
  }

  static updateOrRemoveBackground(
    background: BackgroundProperties | undefined | null,
    componentRef: any,
    renderer: Renderer2,
    accountVariables: Array<AccountVariable>,
    subType?: WidgetComponentSubType,
  ): void {
    const isVideo = Renderer2Helpers.isVideoUrl(background?.imageUrl ?? '');
    const hasImage = !!(background?.imageUrl && !isVideo);
    const hasColor = !!background?.color;

    if (!hasImage && !hasColor) {
      this.removeBackground(componentRef, renderer);
      return;
    }

    this.removeBackground(componentRef, renderer);

    if (hasImage) {
      let imageUrl = background.imageUrl!;
      if (accountVariables) {
        const accountTextVariables = accountVariables.filter((v) => v.type === AccountVariableType.Text);
        imageUrl = ColorHelpers.getTextIfContainsAccountVariable(imageUrl, accountTextVariables);
      }
      let size = 'cover';
      let position = 'center center';
      if (background.size || (subType && subType === WidgetComponentSubType.TextFloating)) {
        size = background.size !== BackgroundImageSize.Fit ? (background.size ?? 'cover') : '100% 100%';
      }
      if (background.position) {
        position = background.position;
      }

      if (hasColor) {
        // Combined image + explicit color: use background shorthand so both are set atomically.
        // The shorthand resets background-color, but that's fine because we supply the color explicitly.
        let backgroundValue: string;
        if (background.color!.includes('gradient')) {
          backgroundValue = `url('${imageUrl}') ${position}/${size} no-repeat, ${background.color!}`;
        } else {
          const colorValue = Renderer2Helpers.extractColorFromProperty(background.color!, accountVariables);
          backgroundValue = colorValue
            ? `${colorValue} url('${imageUrl}') ${position}/${size} no-repeat`
            : `url('${imageUrl}') ${position}/${size} no-repeat`;
        }
        renderer.setStyle(componentRef, 'background', backgroundValue, Renderer2Helpers.FlagsBugfixValue);
      } else {
        // Image only: set individual properties so that any CSS-class background-color
        // (e.g. brand-variable fallback) continues to show through.
        renderer.setStyle(componentRef, 'background-image', `url('${imageUrl}')`, Renderer2Helpers.FlagsBugfixValue);
        renderer.setStyle(componentRef, 'background-size', size, Renderer2Helpers.FlagsBugfixValue);
        renderer.setStyle(componentRef, 'background-position', position, Renderer2Helpers.FlagsBugfixValue);
        renderer.setStyle(componentRef, 'background-repeat', 'no-repeat', Renderer2Helpers.FlagsBugfixValue);
      }
    } else {
      // Color only (no image)
      const backgroundValue = background.color!.includes('gradient')
        ? background.color!
        : Renderer2Helpers.extractColorFromProperty(background.color!, accountVariables);
      renderer.setStyle(componentRef, 'background', backgroundValue, Renderer2Helpers.FlagsBugfixValue);
    }
  }

  static removeBackground(componentRef: any, renderer: Renderer2) {
    renderer.removeStyle(componentRef, 'background', Renderer2Helpers.FlagsBugfixValue);
    renderer.removeStyle(componentRef, 'background-color', Renderer2Helpers.FlagsBugfixValue);
    renderer.removeStyle(componentRef, 'background-image', Renderer2Helpers.FlagsBugfixValue);
    renderer.removeStyle(componentRef, 'background-repeat', Renderer2Helpers.FlagsBugfixValue);
    renderer.removeStyle(componentRef, 'background-size', Renderer2Helpers.FlagsBugfixValue);
    renderer.removeStyle(componentRef, 'background-position', Renderer2Helpers.FlagsBugfixValue);
  }

  static updateOrRemoveBackgroundColor(
    background: BackgroundProperties,
    componentRef: any,
    renderer: Renderer2,
    accountVariables: Array<AccountVariable>,
  ) {
    if (background && background.color) {
      this.updateOrRemoveBackgroundColorValue(background.color, componentRef, renderer, accountVariables);
    } else {
      this.removeBackgroundColor(componentRef, renderer);
    }
  }

  static updateOrRemoveItemGap(gap: string, componentElement: any, renderer: Renderer2) {
    if (gap && componentElement) {
      renderer.setStyle(componentElement, 'gap', gap);
    } else if (componentElement) {
      renderer.removeStyle(componentElement, 'gap');
    }
  }

  static updateOrRemoveBackgroundColorValue(
    backgroundColorString: string,
    componentRef: any,
    renderer: Renderer2,
    accountVariables: Array<AccountVariable>,
  ) {
    if (backgroundColorString) {
      const backgroundColor = backgroundColorString.includes('gradient')
        ? backgroundColorString
        : Renderer2Helpers.extractColorFromProperty(backgroundColorString, accountVariables);
      renderer.removeStyle(componentRef, 'background-color', Renderer2Helpers.FlagsBugfixValue);
      renderer.removeStyle(componentRef, 'background-image', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(componentRef, 'background', backgroundColor, Renderer2Helpers.FlagsBugfixValue);
    } else {
      this.removeBackgroundColor(componentRef, renderer);
    }
  }

  static removeBackgroundColor(componentRef: any, renderer: Renderer2) {
    renderer.removeStyle(componentRef, 'background', Renderer2Helpers.FlagsBugfixValue);
    renderer.removeStyle(componentRef, 'background-color', Renderer2Helpers.FlagsBugfixValue);
    renderer.removeStyle(componentRef, 'background-image', Renderer2Helpers.FlagsBugfixValue);
  }

  static updateJustifyTextContent(alignment: TextAlignment, componentRef: any, renderer: Renderer2) {
    if (alignment) {
      const justifyValue = this.getFlexAlignmentFromTextAlignment(alignment);
      renderer.setStyle(componentRef, 'justify-content', justifyValue, Renderer2Helpers.FlagsBugfixValue);
    }
  }

  static updateAlignItemsFromTextContent(alignment: TextAlignment, componentRef: any, renderer: Renderer2) {
    if (alignment) {
      const justifyValue = this.getFlexAlignmentFromTextAlignment(alignment);
      renderer.setStyle(componentRef, 'align-items', justifyValue, Renderer2Helpers.FlagsBugfixValue);
    }
  }

  static updateAlignSelfFromTextContent(alignment: TextAlignment, componentRef: any, renderer: Renderer2) {
    if (alignment) {
      const justifyValue = this.getFlexAlignmentFromTextAlignment(alignment);
      renderer.setStyle(componentRef, 'align-self', justifyValue, Renderer2Helpers.FlagsBugfixValue);
    }
  }

  static updateAlignSelf(alignment: ComponentAlignment, componentRef: any, renderer: Renderer2) {
    if (alignment) {
      const alignValue = this.getFlexAlignmentFromComponentAlignment(alignment);
      renderer.setStyle(componentRef.nativeElement, 'align-self', alignValue, Renderer2Helpers.FlagsBugfixValue);
    } else {
      renderer.removeStyle(componentRef.nativeElement, 'align-self', Renderer2Helpers.FlagsBugfixValue);
    }
  }

  static updateJustifyContent(alignment: ComponentAlignment, componentRef: ElementRef, renderer: Renderer2) {
    if (alignment) {
      const justifyValue = this.getFlexAlignmentFromComponentAlignment(alignment);
      renderer.setStyle(componentRef.nativeElement, 'justify-content', justifyValue, Renderer2Helpers.FlagsBugfixValue);
    }
  }
  static updateAlignText(alignment: ComponentAlignment, componentRef: any, renderer: Renderer2) {
    if (alignment) {
      for (let i = 0; i < componentRef.length; i++) {
        renderer.setStyle(componentRef[i], 'text-align', alignment, Renderer2Helpers.FlagsBugfixValue);
      }
    }
  }

  static updateFlexGrow(flexGrow: number, componentRef: ElementRef, renderer: Renderer2) {
    if (flexGrow !== undefined && [0, 1].indexOf(flexGrow) !== -1) {
      renderer.setStyle(componentRef.nativeElement, 'flex-grow', flexGrow, Renderer2Helpers.FlagsBugfixValue);
    }
  }

  static updateAlignItems(alignment: ComponentAlignment, componentRef: ElementRef, renderer: Renderer2) {
    if (alignment) {
      const alignValue = this.getFlexAlignmentFromComponentAlignment(alignment);
      renderer.setStyle(componentRef.nativeElement, 'align-items', alignValue, Renderer2Helpers.FlagsBugfixValue);
    }
  }

  static updateDisplayItems(fillPerRow: 3, componentRef: ElementRef, renderer: Renderer2) {
    if (fillPerRow) {
      renderer.setStyle(componentRef.nativeElement, 'display', 'grid');
      renderer.setStyle(
        componentRef.nativeElement,
        'grid-template-columns',
        `repeat(${fillPerRow}, 1fr)`,
        Renderer2Helpers.FlagsBugfixValue,
      );
    } else {
      renderer.setStyle(componentRef.nativeElement, 'display', 'flex');
    }
  }

  static updateComponentAlignment(alignment: ComponentAlignment, componentRef: ElementRef, renderer: Renderer2): void {
    let alignmentValue = 'center';
    if (alignment) {
      if (alignment === ComponentAlignment.Left || alignment === ComponentAlignment.Top) {
        alignmentValue = 'flex-start';
      } else if (alignment === ComponentAlignment.Center) {
        alignmentValue = 'center';
      } else if (alignment === ComponentAlignment.Right || alignment === ComponentAlignment.Bottom) {
        alignmentValue = 'flex-end';
      } else if (alignment === ComponentAlignment.SpaceBetween) {
        alignmentValue = 'space-between';
      } else if (alignment === ComponentAlignment.SpaceAround) {
        alignmentValue = 'space-around';
      } else if (alignment === ComponentAlignment.SpaceEvenly) {
        alignmentValue = 'space-evenly';
      }
    }
    if (componentRef.nativeElement.classList.contains('widget-container-wrapper')) {
      renderer.setStyle(
        componentRef.nativeElement,
        'justify-content',
        alignmentValue,
        Renderer2Helpers.FlagsBugfixValue,
      );
    } else {
      renderer.setStyle(componentRef.nativeElement, 'place-self', alignmentValue, Renderer2Helpers.FlagsBugfixValue);
    }
    //   renderer.setStyle(componentRef.nativeElement, 'place-self', alignmentValue, Renderer2Helpers.FlagsBugfixValue);
  }

  static updateFlexDirection(direction: Direction, componentRef: ElementRef, renderer: Renderer2): void {
    const value = WidgetComponentHelpers.getCssFlexDirection(direction);
    if (value) {
      renderer.setStyle(componentRef.nativeElement, 'flex-direction', value, Renderer2Helpers.FlagsBugfixValue);
    } else {
      renderer.removeStyle(componentRef.nativeElement, 'flex-direction', Renderer2Helpers.FlagsBugfixValue);
    }
  }

  static getFlexAlignmentFromTextAlignment(alignment: TextAlignment): string {
    if (alignment) {
      if (alignment === TextAlignment.Left) {
        return 'flex-start';
      } else if (alignment === TextAlignment.Center) {
        return 'center';
      } else if (alignment === TextAlignment.Right) {
        return 'flex-end';
      } else if (alignment === TextAlignment.Justify) {
        return 'center';
      }
    }
    return undefined;
  }

  static getFlexAlignmentFromComponentAlignment(alignment: ComponentAlignment): string {
    if (alignment) {
      if (alignment === ComponentAlignment.Left || alignment === ComponentAlignment.Top) {
        return 'flex-start';
      } else if (alignment === ComponentAlignment.Center) {
        return 'center';
      } else if (alignment === ComponentAlignment.Right || alignment === ComponentAlignment.Bottom) {
        return 'flex-end';
      }
    }
    return undefined;
  }

  static updateOrRemoveBackgroundImage(
    background: BackgroundProperties,
    componentRef: ElementRef,
    renderer: Renderer2,
    accountVariables: Array<AccountVariable>,
    subType?: WidgetComponentSubType,
  ) {
    const isVideo = Renderer2Helpers.isVideoUrl(background?.imageUrl);
    if (isVideo) {
      this.removeBackgroundImage(componentRef, renderer);
      return;
    }

    if (background && background.imageUrl) {
      renderer.setStyle(componentRef, 'background-repeat', 'no-repeat', Renderer2Helpers.FlagsBugfixValue);
      let backgroundSizeStyle = 'cover';
      let backgroundPositionStyle = 'center center';
      if (background.size || (subType && subType === WidgetComponentSubType.TextFloating)) {
        if (background.size !== BackgroundImageSize.Fit) {
          backgroundSizeStyle = background.size;
        } else {
          backgroundSizeStyle = '100% 100%';
        }
      }
      if (background.position) {
        backgroundPositionStyle = background.position;
      }
      let imageUrlParsed = background.imageUrl;
      if (accountVariables) {
        const accountTextVariables = accountVariables.filter((v) => v.type === AccountVariableType.Text);
        imageUrlParsed = ColorHelpers.getTextIfContainsAccountVariable(imageUrlParsed, accountTextVariables);
      }
      renderer.setStyle(componentRef, 'background-size', backgroundSizeStyle, Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(
        componentRef,
        'background-position',
        backgroundPositionStyle,
        Renderer2Helpers.FlagsBugfixValue,
      );
      renderer.setStyle(
        componentRef,
        'background-image',
        `url('${imageUrlParsed}')`,
        Renderer2Helpers.FlagsBugfixValue,
      );
    } else if (!background?.color?.includes('gradient')) {
      this.removeBackgroundImage(componentRef, renderer);
    }
  }

  static updateOrRemoveBackgroundVideo(
    background: BackgroundProperties,
    componentRef: ElementRef,
    renderer: Renderer2,
    origin: string,
    componentSubType: WidgetComponentSubType,
    radius?: ComponentRadius,
  ) {
    const nativeElement = componentRef.nativeElement;
    if (!nativeElement) {
      return;
    }

    let wrapperElement = nativeElement.querySelector(`:scope > .bg-video-wrapper-${origin}`);
    const directVideoElement = nativeElement.querySelector(`:scope > .bg-video-element-${origin}`);

    const isVideo = Renderer2Helpers.isVideoUrl(background?.imageUrl);

    if (!isVideo) {
      if (wrapperElement) {
        renderer.removeChild(nativeElement, wrapperElement);
      }
      if (directVideoElement) {
        renderer.removeChild(nativeElement, directVideoElement);
      }
      renderer.removeStyle(nativeElement, 'overflow', Renderer2Helpers.FlagsBugfixValue);
      return;
    }

    if (directVideoElement && !wrapperElement) {
      renderer.removeChild(nativeElement, directVideoElement);
    }

    if (!wrapperElement) {
      wrapperElement = renderer.createElement('div');
      renderer.addClass(wrapperElement, `bg-video-wrapper-${origin}`);

      renderer.setStyle(wrapperElement, 'position', 'absolute', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(wrapperElement, 'top', '0', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(wrapperElement, 'left', '0', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(wrapperElement, 'width', '100%', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(wrapperElement, 'height', '100%', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(wrapperElement, 'overflow', 'hidden', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(wrapperElement, 'pointer-events', 'none', Renderer2Helpers.FlagsBugfixValue);
      if (componentSubType === WidgetComponentSubType.ContainerRegular) {
        renderer.setStyle(wrapperElement, 'z-index', '-1', Renderer2Helpers.FlagsBugfixValue);
      } else {
        renderer.removeStyle(wrapperElement, 'z-index');
      }

      renderer.appendChild(nativeElement, wrapperElement);
    }

    this.updateOrRemoveRadius(radius, wrapperElement, renderer);

    let videoElement = wrapperElement.querySelector(`.bg-video-element-${origin}`);
    if (!videoElement) {
      videoElement = renderer.createElement('video');
      renderer.addClass(videoElement, `bg-video-element-${origin}`);

      renderer.setProperty(videoElement, 'autoplay', true);
      renderer.setProperty(videoElement, 'muted', true);
      renderer.setProperty(videoElement, 'loop', true);
      renderer.setProperty(videoElement, 'playsInline', true);

      renderer.setStyle(videoElement, 'position', 'absolute', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(videoElement, 'top', '-1%', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(videoElement, 'left', '-1%', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(videoElement, 'width', '102%', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(videoElement, 'height', '102%', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(videoElement, 'pointer-events', 'none', Renderer2Helpers.FlagsBugfixValue);

      renderer.appendChild(wrapperElement, videoElement);
    }

    this.applyVideoSizingStyles(videoElement, background, renderer);

    if (videoElement.src !== background.imageUrl) {
      renderer.setProperty(videoElement, 'src', background.imageUrl);
      videoElement.load();
    }
  }

  static removeBackgroundImage(componentRef: ElementRef, renderer: Renderer2) {
    renderer.setStyle(componentRef, 'background-image', 'none', Renderer2Helpers.FlagsBugfixValue);
  }

  static updateOrRemoveMaxWidth(width: string, componentRef: ElementRef, renderer: Renderer2) {
    if (width) {
      renderer.setStyle(componentRef.nativeElement, 'max-width', width, Renderer2Helpers.FlagsBugfixValue);
    } else {
      renderer.removeStyle(componentRef.nativeElement, 'max-width');
    }
  }

  static updateOrRemoveMinHeight(height: string, componentRef: ElementRef, renderer: Renderer2) {
    if (height) {
      renderer.setStyle(componentRef.nativeElement, 'min-height', height, Renderer2Helpers.FlagsBugfixValue);
    } else {
      renderer.removeStyle(componentRef.nativeElement, 'min-height');
    }
  }

  static updateOrRemoveHeight(height: string, componentRef: ElementRef, renderer: Renderer2) {
    if (height) {
      renderer.setStyle(componentRef, 'height', height, Renderer2Helpers.FlagsBugfixValue);
    } else {
      renderer.removeStyle(componentRef, 'height');
    }
  }

  static updateOrRemoveWidth(
    width: string,
    componentRef: ElementRef,
    renderer: Renderer2,
    inputContainerRef?: ElementRef,
  ) {
    if (width) {
      if (inputContainerRef) {
        renderer.setStyle(inputContainerRef, 'width', width, Renderer2Helpers.FlagsBugfixValue);
        renderer.setStyle(componentRef, 'width', '100%', Renderer2Helpers.FlagsBugfixValue);
      } else {
        renderer.setStyle(componentRef, 'width', width, Renderer2Helpers.FlagsBugfixValue);
      }
    } else {
      renderer.removeStyle(componentRef, 'width');
    }
  }

  static updateOrRemoveMinWidth(width: string, componentRef: ElementRef, renderer: Renderer2) {
    if (width) {
      renderer.setStyle(componentRef, 'min-width', width, Renderer2Helpers.FlagsBugfixValue);
    } else {
      renderer.removeStyle(componentRef, 'min-width');
    }
  }

  static updateMaxWidth(maxWidth: string, componentRef: ElementRef, renderer: Renderer2) {
    if (maxWidth) {
      renderer.setStyle(componentRef.nativeElement, 'max-width', maxWidth, Renderer2Helpers.FlagsBugfixValue);
    } else {
      renderer.removeStyle(componentRef.nativeElement, 'max-width');
    }
  }

  static updateWidth(width: string, componentRef: ElementRef, renderer: Renderer2) {
    if (width) {
      renderer.setStyle(componentRef, 'width', width, Renderer2Helpers.FlagsBugfixValue);
    }
  }

  static updateOrRemoveMargin(margin: FourSideStylingEntity, componentRef: ElementRef, renderer: Renderer2) {
    if (margin) {
      this.updateMargin(margin, componentRef, renderer);
    } else {
      this.removeMargin(componentRef, renderer);
    }
  }

  static updateMargin(margin: FourSideStylingEntity, componentRef: ElementRef, renderer: Renderer2) {
    if (margin.roundValue) {
      renderer.setStyle(componentRef, 'margin', `${margin.roundValue}`, Renderer2Helpers.FlagsBugfixValue);
    } else {
      if (margin.left) {
        renderer.setStyle(componentRef, 'margin-left', `${margin.left}`, Renderer2Helpers.FlagsBugfixValue);
      }
      if (margin.right) {
        renderer.setStyle(componentRef, 'margin-right', `${margin.right}`, Renderer2Helpers.FlagsBugfixValue);
      }
      if (margin.bottom) {
        renderer.setStyle(componentRef, 'margin-bottom', `${margin.bottom}`, Renderer2Helpers.FlagsBugfixValue);
      }
      if (margin.top) {
        renderer.setStyle(componentRef, 'margin-top', `${margin.top}`, Renderer2Helpers.FlagsBugfixValue);
      }
    }
  }

  static removeMargin(componentRef: ElementRef, renderer: Renderer2) {
    renderer.removeStyle(componentRef, 'margin', Renderer2Helpers.FlagsBugfixValue);
  }

  static removeSideMargin(componentRef: ElementRef, renderer: Renderer2) {
    renderer.setStyle(componentRef.nativeElement, 'margin-left', '0', Renderer2Helpers.FlagsBugfixValue);
    renderer.setStyle(componentRef.nativeElement, 'margin-right', '0', Renderer2Helpers.FlagsBugfixValue);
  }

  static updateOrRemovePadding(
    padding: FourSideStylingEntity,
    componentRef: ElementRef,
    renderer: Renderer2,
    heightIsSmallerThanPadding?,
  ) {
    if (padding && !heightIsSmallerThanPadding) {
      this.updatePadding(padding, componentRef, renderer);
    } else if (!padding || heightIsSmallerThanPadding) {
      this.removePadding(componentRef, renderer);
    }
  }

  static updateOrRemoveShadow(
    shadow: ComponentShadow,
    componentRef: ElementRef,
    renderer: Renderer2,
    accountVariables: Array<AccountVariable>,
  ) {
    if (shadow) {
      this.updateShadow(shadow, componentRef, renderer, accountVariables);
    } else {
      this.removeShadow(componentRef, renderer);
    }
  }

  static updatePadding(padding: FourSideStylingEntity, componentRef: ElementRef, renderer: Renderer2) {
    if (padding.roundValue) {
      renderer.setStyle(componentRef, 'padding', `${padding.roundValue}`, Renderer2Helpers.FlagsBugfixValue);
    } else {
      if (padding.left) {
        renderer.setStyle(componentRef, 'padding-left', `${padding.left}`, Renderer2Helpers.FlagsBugfixValue);
      }
      if (padding.right) {
        renderer.setStyle(componentRef, 'padding-right', `${padding.right}`, Renderer2Helpers.FlagsBugfixValue);
      }
      if (padding.bottom) {
        renderer.setStyle(componentRef, 'padding-bottom', `${padding.bottom}`, Renderer2Helpers.FlagsBugfixValue);
      }
      if (padding.top) {
        renderer.setStyle(componentRef, 'padding-top', `${padding.top}`, Renderer2Helpers.FlagsBugfixValue);
      }
    }
  }

  static removePadding(componentRef: ElementRef, renderer: Renderer2) {
    renderer.removeStyle(componentRef, 'padding', Renderer2Helpers.FlagsBugfixValue);
  }

  static updateShadow(
    shadow: ComponentShadow,
    componentRef: ElementRef,
    renderer: Renderer2,
    accountVariables: Array<AccountVariable>,
  ) {
    let shadowString = shadow ? '' : 'none';
    if (shadow) {
      const shadowColor = Renderer2Helpers.extractColorFromProperty(shadow.color, accountVariables);
      shadowString += (shadow.horizontalLength ? shadow.horizontalLength : '0') + ' ';
      shadowString += (shadow.verticalLength ? shadow.verticalLength : '0') + ' ';
      shadowString += (shadow.blur ? shadow.blur : '0') + ' ';
      shadowString += (shadow.spread ? shadow.spread : '0') + ' ';
      shadowString += shadowColor ? shadowColor : '#FFFFFFFF';
    }
    renderer.setStyle(componentRef, '-webkit-box-shadow', shadowString, Renderer2Helpers.FlagsBugfixValue);
    renderer.setStyle(componentRef, '-moz-box-shadow', shadowString, Renderer2Helpers.FlagsBugfixValue);
    renderer.setStyle(componentRef, 'box-shadow', shadowString, Renderer2Helpers.FlagsBugfixValue);
  }

  static removeShadow(componentRef: ElementRef, renderer: Renderer2) {
    renderer.setStyle(componentRef, '-webkit-box-shadow', 'none', Renderer2Helpers.FlagsBugfixValue);
    renderer.setStyle(componentRef, '-moz-box-shadow', 'none', Renderer2Helpers.FlagsBugfixValue);
    renderer.setStyle(componentRef, 'box-shadow', 'none', Renderer2Helpers.FlagsBugfixValue);
  }

  static updateOrRemoveDropShadow(
    shadow: ComponentShadow,
    componentRef: ElementRef,
    renderer: Renderer2,
    accountVariables: Array<AccountVariable>,
  ) {
    if (shadow) {
      this.updateDropShadow(shadow, componentRef, renderer, accountVariables);
    } else {
      this.removeDropShadow(componentRef, renderer);
    }
  }

  static updateDropShadow(
    shadow: ComponentShadow,
    componentRef: ElementRef,
    renderer: Renderer2,
    accountVariables: Array<AccountVariable>,
  ) {
    let shadowString = shadow ? '' : 'none';
    if (shadow) {
      const shadowColor = Renderer2Helpers.extractColorFromProperty(shadow.color, accountVariables);
      shadowString += (shadow.horizontalLength ? shadow.horizontalLength : '0') + ' ';
      shadowString += (shadow.verticalLength ? shadow.verticalLength : '0') + ' ';
      shadowString += (shadow.blur ? shadow.blur : '0') + ' ';
      shadowString += shadowColor ? shadowColor : '#FFFFFFFF';
    }
    renderer.setStyle(componentRef, 'filter', `drop-shadow(${shadowString})`, Renderer2Helpers.FlagsBugfixValue);
  }

  static removeDropShadow(componentRef: ElementRef, renderer: Renderer2) {
    renderer.setStyle(componentRef, 'filter', 'none');
  }

  static updateOrRemoveBorder(
    border: ComponentBorder,
    componentRef: ElementRef,
    renderer: Renderer2,
    accountVariables: Array<AccountVariable>,
  ) {
    if (border) {
      this.updateBorder(border, componentRef, renderer, accountVariables);
    } else {
      this.removeBorder(componentRef, renderer);
    }
  }

  static removeBorder(componentRef: ElementRef, renderer: Renderer2) {
    renderer.removeStyle(componentRef, 'border', Renderer2Helpers.FlagsBugfixValue);
  }

  static updateBorder(
    border: ComponentBorder,
    componentRef: ElementRef,
    renderer: Renderer2,
    accountVariables: Array<AccountVariable>,
  ) {
    if (border.style) {
      renderer.setStyle(componentRef, 'border-style', border.style, Renderer2Helpers.FlagsBugfixValue);
    } else {
      renderer.setStyle(componentRef, 'border-style', 'solid', Renderer2Helpers.FlagsBugfixValue);
    }
    if (border.color) {
      const borderColor = Renderer2Helpers.extractColorFromProperty(border.color, accountVariables);
      renderer.setStyle(componentRef, 'border-color', borderColor, Renderer2Helpers.FlagsBugfixValue);
    }
    if (border.roundValue) {
      renderer.setStyle(componentRef, 'border-width', `${border.roundValue}`, Renderer2Helpers.FlagsBugfixValue);
    } else {
      if (border.left) {
        renderer.setStyle(componentRef, 'border-left-width', `${border.left}`, Renderer2Helpers.FlagsBugfixValue);
      }
      if (border.right) {
        renderer.setStyle(componentRef, 'border-right-width', `${border.right}`, Renderer2Helpers.FlagsBugfixValue);
      }
      if (border.bottom) {
        renderer.setStyle(componentRef, 'border-bottom-width', `${border.bottom}`, Renderer2Helpers.FlagsBugfixValue);
      }
      if (border.top) {
        renderer.setStyle(componentRef, 'border-top-width', `${border.top}`, Renderer2Helpers.FlagsBugfixValue);
      }
    }
  }

  static updateOrRemoveRadius(radius: ComponentRadius, componentRef: ElementRef, renderer: Renderer2) {
    if (radius) {
      this.updateRadius(radius, componentRef, renderer);
    } else {
      this.removeRadius(componentRef, renderer);
    }
  }

  static updateFloatingComponentPosition(floatingComponent: WidgetComponent, width, element: any, renderer: Renderer2) {
    const isHorizontalCenterPosition =
      floatingComponent.positionCorner === PositionCorner.TopCenter ||
      floatingComponent.positionCorner === PositionCorner.BottomCenter;
    let propertyX, propertyY, propertiesToRemove;
    if (floatingComponent.positionCorner === PositionCorner.TopRight) {
      propertyX = 'right';
      propertyY = 'top';
      propertiesToRemove = ['left', 'bottom'];
    } else if (floatingComponent.positionCorner === PositionCorner.BottomRight) {
      propertyX = 'right';
      propertyY = 'bottom';
      propertiesToRemove = ['left', 'top'];
    } else if (floatingComponent.positionCorner === PositionCorner.BottomLeft) {
      propertyX = 'left';
      propertyY = 'bottom';
      propertiesToRemove = ['right', 'top'];
    } else if (floatingComponent.positionCorner === PositionCorner.TopLeft) {
      propertyX = 'left';
      propertyY = 'top';
      propertiesToRemove = ['right', 'bottom'];
    } else if (isHorizontalCenterPosition) {
      propertiesToRemove = ['right'];
      propertyX = 'left';
      if (floatingComponent.positionCorner === PositionCorner.BottomCenter) {
        propertiesToRemove.push('top');
        propertyY = 'bottom';
      } else {
        propertiesToRemove.push('bottom');
        propertyY = 'top';
      }
    } else {
      throw new Error('Unsupported position corner: ' + floatingComponent.positionCorner);
    }

    const widthInPixels = WidgetComponentHelpers.extractNumberFromPixelOrPercentString(width);
    const positionXCss = isHorizontalCenterPosition
      ? `calc(50% - ${widthInPixels / 2}px)`
      : floatingComponent.positionX + 'px';
    renderer.setStyle(element, propertyX, positionXCss, Renderer2Helpers.FlagsBugfixValue);
    renderer.setStyle(element, propertyY, floatingComponent.positionY + 'px', Renderer2Helpers.FlagsBugfixValue);
    propertiesToRemove.forEach((property) =>
      renderer.removeStyle(element, property, Renderer2Helpers.FlagsBugfixValue),
    );
  }

  static calcMaxWidth(value, componentRef: ElementRef, renderer: Renderer2) {
    if (value) {
      const erdgesr = `calc(100% - ${value})`;
      renderer.setStyle(componentRef, 'max-width', erdgesr, Renderer2Helpers.FlagsBugfixValue);
    } else {
      renderer.removeStyle(componentRef, 'max-width');
    }
  }

  static getRadius(radius: ComponentRadius): string | null {
    if (radius.roundValue) {
      return `${radius.roundValue}`;
    }
    if (!radius.topLeft && !radius.topRight && !radius.bottomLeft && !radius.bottomRight) {
      return null;
    }

    const topLeft = radius.topLeft || '0';
    const topRight = radius.topRight || '0';
    const bottomRight = radius.bottomRight || '0';
    const bottomLeft = radius.bottomLeft || '0';

    return `${topLeft} ${topRight} ${bottomRight} ${bottomLeft}`;
  }

  static updateRadius(radius: ComponentRadius, componentRef: ElementRef, renderer: Renderer2) {
    const value = this.getRadius(radius);
    if (value) {
      renderer.setStyle(componentRef, 'border-radius', value, Renderer2Helpers.FlagsBugfixValue);
    }
  }

  static removeRadius(componentRef: ElementRef, renderer: Renderer2) {
    renderer.removeStyle(componentRef, 'border-radius', Renderer2Helpers.FlagsBugfixValue);
  }

  static setOverflow(style: IFrameOverflow, componentRef: ElementRef, renderer: Renderer2) {
    let overflow;
    if (style === IFrameOverflow.Hide) {
      overflow = 'hidden';
      renderer.setAttribute(componentRef, 'scrolling', 'no');
    } else {
      overflow = 'scroll';
      renderer.setAttribute(componentRef, 'scrolling', 'yes');
    }
    renderer.setStyle(componentRef, 'overflow', overflow, Renderer2Helpers.FlagsBugfixValue);
  }

  static updateRotationOnEdgeCanvas(state: WidgetState, nativeElement: HTMLElement, renderer: Renderer2) {
    if (!nativeElement || !state) return;
    const position = state.position;
    let rotation = state.rotation ?? 0;
    const height = nativeElement.offsetHeight;
    const width = nativeElement.offsetWidth;
    const moveTo = -(width / 2) + height / 2;
    this.resetCanvas(nativeElement, renderer);
    if (position === WidgetPosition.Right) {
      rotation = rotation - 90;
      renderer.setStyle(nativeElement, 'right', `${moveTo}px`);
    } else if (position === WidgetPosition.Left) {
      rotation = rotation - 90;
      renderer.setStyle(nativeElement, 'left', `${moveTo}px`);
    }

    if (rotation) {
      renderer.setStyle(nativeElement, 'transform', `rotate(${rotation}deg)`);
    } else {
      renderer.removeStyle(nativeElement, 'transform', Renderer2Helpers.FlagsBugfixValue);
    }
  }

  static updateRotationOnCanvas(state: WidgetState, nativeElement: HTMLElement, renderer: Renderer2) {
    if (!nativeElement) return;
    const rotation = state?.rotation;

    if (rotation) {
      renderer.setStyle(nativeElement, 'transform', `rotate(${rotation}deg)`);
    } else {
      renderer.removeStyle(nativeElement, 'transform', Renderer2Helpers.FlagsBugfixValue);
    }
    this.resetCanvas(nativeElement, renderer);
  }

  static updateFlipAlignment(flipAlignment, nativeElement: HTMLElement, renderer: Renderer2) {
    let scaleY = flipAlignment === FlipAlignment.Vertical ? -1 : 1;
    let scaleX = flipAlignment === FlipAlignment.Horizontal ? -1 : 1;
    if (!flipAlignment) {
      scaleX = 1;
      scaleY = 1;
    }
    const transformValue = `scale(${scaleX}, ${scaleY})`;
    renderer.setStyle(nativeElement, 'transform', transformValue);
  }

  private static resetCanvas(nativeElement: HTMLElement, renderer: Renderer2) {
    renderer.removeStyle(nativeElement, 'right');
    renderer.removeStyle(nativeElement, 'left');
  }

  static setIframeUrl(url: string, componentRef: ElementRef, renderer: Renderer2) {
    renderer.setAttribute(componentRef, 'src', url);
  }

  static flexAlignItems(alignItemsValue: string, componentRef: ElementRef, renderer: Renderer2) {
    renderer.setStyle(componentRef.nativeElement, 'align-items', alignItemsValue, Renderer2Helpers.FlagsBugfixValue);
  }

  static flexJustifyContent(justifyContentValue: string, componentRef: ElementRef, renderer: Renderer2) {
    renderer.setStyle(
      componentRef.nativeElement,
      'align-items',
      justifyContentValue,
      Renderer2Helpers.FlagsBugfixValue,
    );
  }

  public static getStyleFromFourSideStyling(value: FourSideStylingEntity): string {
    if (value?.roundValue) {
      return value.roundValue.toString();
    }
    const top = value?.top ?? '0';
    const right = value?.right ?? '0';
    const bottom = value?.bottom ?? '0';
    const left = value?.left ?? '0';

    return `${top} ${right} ${bottom} ${left}`;
  }

  private static applyVideoSizingStyles(videoElement: any, background: BackgroundProperties, renderer: Renderer2) {
    let objectFit = 'cover';
    if (background.size) {
      objectFit = background.size === BackgroundImageSize.Fit ? 'fill' : background.size;
    }
    renderer.setStyle(videoElement, 'object-fit', objectFit);

    if (background.position) {
      renderer.setStyle(videoElement, 'object-position', background.position);
    } else {
      renderer.setStyle(videoElement, 'object-position', 'center center');
    }
  }

  public static isVideoUrl(mediaUrl: string): boolean {
    const videoRegExp = new RegExp('\\.(mp4|webm)(\\?.*)?$', 'i');

    return mediaUrl && videoRegExp.test(mediaUrl);
  }

  static updateOrRemoveScratchOverlay(
    scratch: ScratchProperties | undefined | null,
    targetElement: HTMLElement | undefined | null,
    renderer: Renderer2,
    accountVariables: Array<AccountVariable>,
  ): void {
    if (!targetElement) {
      return;
    }
    const existingOverlay = targetElement.querySelector(':scope > .scratch-overlay-static');

    if (!scratch) {
      if (existingOverlay) {
        renderer.removeChild(targetElement, existingOverlay);
      }
      return;
    }

    renderer.setStyle(targetElement, 'position', 'relative', Renderer2Helpers.FlagsBugfixValue);

    let overlay = existingOverlay;
    if (!overlay) {
      overlay = renderer.createElement('div');
      renderer.addClass(overlay, 'scratch-overlay-static');
      renderer.setStyle(overlay, 'position', 'absolute', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(overlay, 'top', '0', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(overlay, 'left', '0', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(overlay, 'width', '100%', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(overlay, 'height', '100%', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(overlay, 'pointer-events', 'none', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(overlay, 'z-index', '1', Renderer2Helpers.FlagsBugfixValue);
      renderer.appendChild(targetElement, overlay);
    }

    let textScratch = overlay.querySelector(':scope > .scratch-overlay-text');
    if (!textScratch) {
      textScratch = renderer.createElement('div');
      renderer.addClass(textScratch, 'scratch-overlay-text');
      renderer.setStyle(overlay, 'overflow', 'hidden', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(textScratch, 'position', 'absolute', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(textScratch, 'top', '50%', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(textScratch, 'left', '8%', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(textScratch, 'right', '8%', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(textScratch, 'transform', 'translateY(-50%)', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(textScratch, 'font-weight', 'bold', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(textScratch, 'font-size', '14px', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(textScratch, 'line-height', '1.2', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(textScratch, 'text-align', 'center', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(textScratch, 'white-space', 'normal', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(textScratch, 'overflow-wrap', 'break-word', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(textScratch, 'word-break', 'break-word', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(textScratch, 'pointer-events', 'none', Renderer2Helpers.FlagsBugfixValue);
      renderer.appendChild(overlay, textScratch);
    }

    renderer.setProperty(textScratch, 'textContent', scratch.text ?? 'SCRATCH TO REVEAL');
    const textColor = scratch.textColor
      ? Renderer2Helpers.extractColorFromProperty(scratch.textColor, accountVariables)
      : null;
    renderer.setStyle(
      textScratch,
      'color',
      textColor || scratch.textColor || '#000',
      Renderer2Helpers.FlagsBugfixValue,
    );

    Renderer2Helpers.updateScratchOverlayFont(targetElement, renderer);

    if (scratch.imageUrl) {
      let imageUrl = scratch.imageUrl;
      if (accountVariables) {
        const accountTextVariables = accountVariables.filter((v) => v.type === AccountVariableType.Text);
        imageUrl = ColorHelpers.getTextIfContainsAccountVariable(imageUrl, accountTextVariables);
      }
      const size = scratch.size !== BackgroundImageSize.Fit ? (scratch.size ?? 'cover') : '100% 100%';
      const position = scratch.position ?? 'center center';
      renderer.removeStyle(overlay, 'background-color', Renderer2Helpers.FlagsBugfixValue);
      renderer.removeStyle(overlay, 'background', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(overlay, 'background-image', `url('${imageUrl}')`, Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(overlay, 'background-size', size, Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(overlay, 'background-position', position, Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(overlay, 'background-repeat', 'no-repeat', Renderer2Helpers.FlagsBugfixValue);
    } else if (scratch.color) {
      const backgroundValue = scratch.color.includes('gradient')
        ? scratch.color
        : Renderer2Helpers.extractColorFromProperty(scratch.color, accountVariables);
      renderer.removeStyle(overlay, 'background-image', Renderer2Helpers.FlagsBugfixValue);
      renderer.removeStyle(overlay, 'background-color', Renderer2Helpers.FlagsBugfixValue);
      renderer.setStyle(overlay, 'background', backgroundValue ?? scratch.color, Renderer2Helpers.FlagsBugfixValue);
    } else {
      renderer.removeStyle(overlay, 'background-image', Renderer2Helpers.FlagsBugfixValue);
      renderer.removeStyle(overlay, 'background-color', Renderer2Helpers.FlagsBugfixValue);
      renderer.removeStyle(overlay, 'background', Renderer2Helpers.FlagsBugfixValue);
    }
  }

  // Siblings of .text-content/.component-text-box, not descendants, so font/radius don't cascade
  // via CSS — copied from the DOM instead of component.radius/textProperties
  static updateScratchOverlayFont(targetElement: HTMLElement | undefined | null, renderer: Renderer2): void {
    if (!targetElement) {
      return;
    }
    const overlay = targetElement.querySelector(':scope > .scratch-overlay-static');
    const textScratch = overlay?.querySelector(':scope > .scratch-overlay-text');
    if (!overlay || !textScratch) {
      return;
    }
    setTimeout(() => {
      const textElement: HTMLElement | null = targetElement.querySelector('.text-content');
      const inheritedStyle = textElement ? getComputedStyle(textElement) : null;
      const inheritedFontFamily = inheritedStyle?.fontFamily;
      if (inheritedFontFamily) {
        renderer.setStyle(textScratch, 'font-family', inheritedFontFamily, Renderer2Helpers.FlagsBugfixValue);
      } else {
        renderer.removeStyle(textScratch, 'font-family', Renderer2Helpers.FlagsBugfixValue);
      }
      renderer.setStyle(
        textScratch,
        'font-size',
        inheritedStyle?.fontSize || '14px',
        Renderer2Helpers.FlagsBugfixValue,
      );

      const boxElement = targetElement.querySelector<HTMLElement>('.component-text-box') ?? targetElement;
      renderer.setStyle(
        overlay,
        'border-radius',
        getComputedStyle(boxElement).borderRadius,
        Renderer2Helpers.FlagsBugfixValue,
      );
    }, 0);
  }
}
